import Axios from 'axios';
import axiosRetry from 'axios-retry';
import get from 'lodash/get';
import * as Sentry from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import { makeAutoObservable } from 'mobx';
import getAuth0Client from '~/services/auth0';
import toasts from '~/utils/toasts';

const axios = Axios.create();

// Refresh this long before expiry so a token never expires mid-request.
const EXPIRY_MARGIN_SECONDS = 60;
const SESSION_ENDED_ERRORS = ['login_required', 'missing_refresh_token', 'invalid_grant'];

export class TokenStore {
  token = null as string | null;

  // Shared so concurrent callers trigger a single Auth0 refresh.
  private refreshing: Promise<void> | null = null;

  constructor() {
    makeAutoObservable<TokenStore, 'refreshing'>(this, { refreshing: false });
  }

  setToken(token: string) {
    this.token = token;
  }

  getAppToken() {
    return this.token ? `Bearer ${this.token}` : null;
  }

  getToken() {
    return this.token;
  }

  isAuthorized() {
    return Boolean(this.token);
  }

  expiresWithin(seconds: number) {
    if (!this.token) return true;
    const { exp } = jwtDecode(this.token);
    return !exp || Date.now() / 1000 > exp - seconds;
  }

  isExpired() {
    return this.expiresWithin(0);
  }

  /**
   * Returns a token valid for at least EXPIRY_MARGIN_SECONDS, refreshing it first if needed.
   * `force` refreshes regardless of the local expiry check (e.g. after the server rejected it).
   */
  async getFreshToken(force = false) {
    if (!this.token) return null;
    if (force || this.expiresWithin(EXPIRY_MARGIN_SECONDS)) {
      this.refreshing ??= this.refreshToken().finally(() => {
        this.refreshing = null;
      });
      await this.refreshing;
    }
    return this.token;
  }

  async refreshToken() {
    const client = getAuth0Client();
    try {
      await client.getTokenSilently({ cacheMode: 'off' });
    } catch (e: any) {
      // The Auth0 session itself is gone — no token can be refreshed without logging in again.
      if (SESSION_ENDED_ERRORS.includes(e?.error)) {
        client.loginWithRedirect();
      }
      throw e;
    }
    const claims = await client.getIdTokenClaims();
    this.setToken(claims?.__raw as string);
  }
}

export const token = new TokenStore();

axiosRetry(axios, {
  retries: 3,
  retryDelay: (retryCount, error) => axiosRetry.exponentialDelay(retryCount, error, 1000)
});

axios.interceptors.request.use(
  (config) => {
    Sentry.setContext('axios', config);
    return config;
  },
  (error) => {
    Sentry.setContext('axios', error.config);
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    Sentry.setContext('axios', response);
    return response;
  },
  async (error) => {
    const orginalRequest = error.config;
    if (Axios.isCancel(error)) {
      return Promise.reject(error);
    }
    if (error.response?.status === 500) {
      toasts.error('There was a problem processing your request, please try again later or contact ssupport');
    } else if (error.response?.status === 403 || error.response?.data === 'Unauthorized') {
      // Retry once with a forcibly refreshed token — don't trust the local expiry check, the
      // client clock may be off. Only for requests that were authorized in the first place.
      if (orginalRequest?.headers?.Authorization && !orginalRequest._authRetried) {
        orginalRequest._authRetried = true;
        await token.getFreshToken(true);
        orginalRequest.headers.Authorization = token.getAppToken();
        return axios.request(orginalRequest);
      }
    } else {
      let errorResponse;
      if (get(error, 'response.data.errors.detail')) {
        errorResponse = get(error, 'response.data.errors.detail');
      } else if (get(error, 'error.response.data.error')) {
        errorResponse = get(error, 'error.response.data.error');
      } else if (error.request) {
        errorResponse = error.request.message || error.request.statusText;
      } else {
        errorResponse = error.message;
      }
      if (errorResponse) {
        toasts.error(errorResponse);
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
